import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Link from '@ux/link';
import { openOptOutModal } from './OptOutModal';

/**
 * @typedef {Object} LinkDefinition
 * @prop {string} [href] URL reference to link to (required if no `onClick` is specified)
 * @prop {string} [eid] Event ID for the link
 * @prop {function} [onClick] Function to run when link is clicked (required if no `href` is specified)
 * @prop {string} key Link key
 */

/**
 * Open opt-out modal on click of the opt-out button
 *
 * @param {React.MouseEvent} evt Event
 * @private
 */
function onOptOutClick(evt) {
  evt.preventDefault();
  openOptOutModal();
}

/**
* Returns sidebar links.
*
* @param {Object} props Props
* @param {LinkDefinition} props.link Object providing link data.
* @param {function} props.onClose Function to call when link is clicked.
* @param {string} props.className className for the link
*
* @returns {React.ReactElement} Single link for app-sidebar to be mapped in sidebar nav and account-tray nav.
*/
export default function NavigationLink({ link, onClose, className, children, sidebarNavLinkHandler }) {

  const customHandler = useMemo(() => {
    const fn = sidebarNavLinkHandler && sidebarNavLinkHandler(link.key, link);
    return (fn instanceof Function) ? fn : null;
  }, [link, sidebarNavLinkHandler]);

  const onClick = useCallback(function () {
    if (customHandler) {
      customHandler(...arguments);
    }
    if (link.key === 'opt_out_label') return onOptOutClick(...arguments);
    if (link.onClick) link.onClick(...arguments);
    if (onClose && onClose()) {
      onClose(...arguments);
    }
  }, [link, onClose, customHandler]);

  if (children) {
    return <Link
      href={ link.href || '#' }
      target={ link.target }
      data-eid={ link.eid }
      className={ className }
      onClick={ (link.onClick || customHandler) && onClick }
      onClicked={ link.key === 'opt_out_label' ? onOptOutClick : onClose }>
      { children }
    </Link>;
  }

  return null;
}

NavigationLink.propTypes = {
  link: PropTypes.shape({
    href: PropTypes.string,
    target: PropTypes.string,
    eid: PropTypes.string,
    onClick: PropTypes.func,
    key: PropTypes.string
  }),
  onClose: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
  sidebarNavLinkHandler: PropTypes.func
};
